import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { invoiceAPI, clientAPI } from "../lib/api";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel, debounce } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Download,
  Loader2,
  FileText,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    client_id: "",
    is_proforma: false,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [filters, pagination.page]);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.list();
      setClients(response.data);
    } catch (error) {
      console.error("Failed to load clients");
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      };
      if (search) params.search = search;

      const response = await invoiceAPI.list(params);
      setInvoices(response.data.invoices || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.total || 0,
        pages: response.data.pages || 0,
      }));
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = debounce(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchInvoices();
  }, 300);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearch();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleTabChange = (value) => {
    setFilters((prev) => ({ ...prev, is_proforma: value === "proforma" }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) {
      return;
    }

    try {
      await invoiceAPI.delete(invoice.id);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const response = await invoiceAPI.getPDF(invoice.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number.replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-500">Manage tax and proforma invoices</p>
        </div>
        {canEdit() && (
          <Button onClick={() => navigate("/invoices/new")} data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tax" onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="tax" data-testid="tax-invoices-tab">Tax Invoices</TabsTrigger>
          <TabsTrigger value="proforma" data-testid="proforma-invoices-tab">Proforma Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="tax" className="mt-4 space-y-4">
          <InvoiceFilters
            search={search}
            onSearchChange={handleSearchChange}
            filters={filters}
            clients={clients}
            onFilterChange={handleFilterChange}
          />
          <InvoiceTable
            invoices={invoices}
            loading={loading}
            onView={(inv) => navigate(`/invoices/${inv.id}`)}
            onEdit={(inv) => navigate(`/invoices/${inv.id}/edit`)}
            onDelete={handleDelete}
            onDownload={handleDownloadPDF}
            canEdit={canEdit}
            isAdmin={isAdmin}
            pagination={pagination}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          />
        </TabsContent>

        <TabsContent value="proforma" className="mt-4 space-y-4">
          <InvoiceFilters
            search={search}
            onSearchChange={handleSearchChange}
            filters={filters}
            clients={clients}
            onFilterChange={handleFilterChange}
          />
          <InvoiceTable
            invoices={invoices}
            loading={loading}
            onView={(inv) => navigate(`/invoices/${inv.id}`)}
            onEdit={(inv) => navigate(`/invoices/${inv.id}/edit`)}
            onDelete={handleDelete}
            onDownload={handleDownloadPDF}
            canEdit={canEdit}
            isAdmin={isAdmin}
            pagination={pagination}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InvoiceFilters({ search, onSearchChange, filters, clients, onFilterChange }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by invoice number or PO..."
              value={search}
              onChange={onSearchChange}
              className="pl-10"
              data-testid="search-invoices"
            />
          </div>
          <Select
            value={filters.status || "all"}
            onValueChange={(value) => onFilterChange("status", value === "all" ? "" : value)}
          >
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filters.client_id || "all"}
            onValueChange={(value) => onFilterChange("client_id", value === "all" ? "" : value)}
          >
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="All Clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceTable({
  invoices,
  loading,
  onView,
  onEdit,
  onDelete,
  onDownload,
  canEdit,
  isAdmin,
  pagination,
  onPageChange,
}) {
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (invoices.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">No invoices found</h3>
          <p className="text-slate-500">Try adjusting your filters or create a new invoice</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id} className="cursor-pointer hover:bg-slate-50">
                <TableCell
                  className="font-mono text-sm"
                  onClick={() => onView(invoice)}
                >
                  {invoice.invoice_number}
                </TableCell>
                <TableCell
                  className="max-w-[150px] truncate"
                  onClick={() => onView(invoice)}
                >
                  {invoice.client_name}
                </TableCell>
                <TableCell onClick={() => onView(invoice)}>
                  {formatDate(invoice.invoice_date)}
                </TableCell>
                <TableCell onClick={() => onView(invoice)}>
                  {formatDate(invoice.due_date)}
                </TableCell>
                <TableCell className="text-right font-mono" onClick={() => onView(invoice)}>
                  {formatCurrency(invoice.grand_total)}
                </TableCell>
                <TableCell
                  className={`text-right font-mono ${invoice.balance_due > 0 ? "text-amber-600 font-medium" : ""}`}
                  onClick={() => onView(invoice)}
                >
                  {formatCurrency(invoice.balance_due)}
                </TableCell>
                <TableCell onClick={() => onView(invoice)}>
                  <Badge className={getStatusColor(invoice.status)}>
                    {getStatusLabel(invoice.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onView(invoice)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDownload(invoice)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </DropdownMenuItem>
                      {canEdit() && invoice.status !== "paid" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onEdit(invoice)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        </>
                      )}
                      {isAdmin() && invoice.status !== "paid" && (
                        <DropdownMenuItem
                          onClick={() => onDelete(invoice)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-slate-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => onPageChange(pagination.page - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => onPageChange(pagination.page + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
